import * as React from 'react'
import moment from 'moment'

import { ImiExportModel } from '@/app/models/export/imiExportModel'

import { CountryModel } from '@/app/models/timesheet/countryModel'
import { validateImiModel } from '@/app/models/export/imiExportModelValidator'
import { OperationType } from '@/app/constants/operationType'
import { TransportType } from '@/app/constants/transportType'
import { getOperationTypeName } from '@/app/common/converters/export/operationTypeToTextConverter'
import { getTransportTypeName } from '@/app/common/converters/export/transportTypeToTextConverter'
import { getDifferenceBetweenDatesInDays } from '@/app/common/dateHelper'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryDriversByIds } from '@/lib/api/company/drivers'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import PrintoutDriversTable from '@/modules/printouts/components/PrintoutDriversTable'
import { v4 as uuidv4 } from 'uuid'
import { TaskType } from '@/app/constants/taskType'
import { useImiExport } from '../services/useExportService'
import { useQueryVehicles } from '../services/useQueryVehicles'
import { useQueryExportCountries } from '../services/useQueryExportCountries'
import { toast } from '@/lib/toasts/api'
import { useTask } from '@/modules/shared/context/TaskContext'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import {
  useImiExportErrors,
  useImiExportErrorsStore,
} from '../stores/imiExportErrorsStore'

type MatchParams = {
  companyId: string
  importId?: string
}

const OPERATION_TYPES: Array<OperationType> = [
  OperationType.Cabotage,
  OperationType.InternationalCarriage,
]

const TRANSPORT_TYPES: Array<TransportType> = [
  TransportType.CarriageOfGoods,
  TransportType.CarriageOfPeople,
]

const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'ES',
  'CY', 'GR', 'NL', 'IE', 'LT', 'LV', 'LU', 'DE', 'PT', 'RO',
  'SK', 'SE', 'IT', 'HU', 'GB', 'SI', 'MT', 'NO', 'LI', 'IS',
]

const IMIListPageContent: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const { t } = useLocalize()
  const { params } = match
  const { companyId, importId } = params
  const exportErrors = useImiExportErrors(companyId)
  const { selectedGroup } = useTable()
  const imiExportMutation = useImiExport(companyId)
  const { data: vehicles = [], isLoading: vehiclesLoading } = useQueryVehicles({ companyId })
  const { data: countries = [], isLoading: countriesLoading } = useQueryExportCountries({ companyId })
  const { addTask } = useTask()

  // Get checked drivers from TableContext
  const [checkedDrivers, setCheckedDrivers] = React.useState<string[]>([])

  // Sync TableContext selection with local state
  React.useEffect(() => {
    const ids = (selectedGroup?.ids || []).map((id) => id.toString())
    setCheckedDrivers(ids)
  }, [selectedGroup?.ids])

  const { drivers } = useQueryDriversByIds({
    companyId,
    driverIds: checkedDrivers,
    withPersonalData: true,
  })

  const [models, setModels] = React.useState<Array<ImiExportModel>>([])
  const [submitted, setSubmitted] = React.useState(false)
  const [defaultDateFrom, setDefaultDateFrom] = React.useState(moment())
  const [defaultDateTo, setDefaultDateTo] = React.useState(
    moment().add(6, 'months').add(-1, 'days'),
  )
  const [defaultCountry, setDefaultCountry] = React.useState<CountryModel[]>([])
  const [defaultOperationType, setDefaultOperationType] = React.useState<OperationType[]>([])
  const [defaultTransportType, setDefaultTransportType] = React.useState<TransportType[]>([])
  const [defaultVehicles, setDefaultVehicles] = React.useState<string[]>([])

  React.useEffect(() => {
    if (checkedDrivers.length > 0 && drivers.length > 0) {
      const newModels: Array<ImiExportModel> = checkedDrivers.map((driverId, i) => {
        const driver = drivers.find((d) => d.id.toString() === driverId)
        if (!driver) return null

        const validationResult = validateImiModel(driver)
        return {
          driver,
          operationType: [],
          transportType: [],
          isValid: validationResult.length === 0,
          vehicles: [],
          country: [],
          lp: i,
          dateFrom: defaultDateFrom.clone(),
          dateTo: defaultDateTo.clone(),
          validation: validationResult,
        }
      }).filter((m): m is ImiExportModel => m !== null)

      const orderedModels = newModels.sort((a, b) => {
        if (a.driver.surname < b.driver.surname) return -1
        if (a.driver.surname > b.driver.surname) return 1
        return 0
      })

      orderedModels.forEach((model, i) => {
        model.lp = i
      })

      setModels(orderedModels)
    } else {
      setModels([])
    }
  }, [checkedDrivers, drivers, defaultDateFrom, defaultDateTo])

  const handleDriversSelectionChange = React.useCallback(
    (selectedIds: number[]) => {
      // Selection is handled by TableContext automatically
      setCheckedDrivers(selectedIds.map((id) => id.toString()))
    },
    [],
  )

  const memoizedDefaultSelectedDrivers = React.useMemo(
    () => checkedDrivers.map((id) => parseInt(id)).filter((id) => !isNaN(id)),
    [checkedDrivers],
  )

  const validateModel = (model: ImiExportModel): boolean => {
    if (!model.validation || model.validation.length > 0) return false
    if (!model.vehicles || model.vehicles.length === 0) return false
    if (!model.dateFrom || !model.dateTo) return false
    const duration = getDifferenceBetweenDatesInDays(model.dateFrom, model.dateTo)
    if (duration === '-' || duration > 183) return false
    if (!model.transportType || model.transportType.length === 0) return false
    if (!model.operationType || model.operationType.length === 0) return false
    if (!model.country || model.country.length === 0) return false
    return true
  }

  const handleExport = async () => {
    setSubmitted(true)

    const allValid = models.every(validateModel)
    if (!allValid || models.length === 0) {
      toast.warning({
        text: t('imiExport.validation-error'),
        content: t('imiExport.validation-error-details'),
      })
      return
    }

    const taskId = uuidv4()
    const actualImportId = importId || 'default'

    try {
      const serializedModels = models.map((m) => ({
        ...m,
        dateFrom: (m.dateFrom as any)?.format?.('YYYY-MM-DD') ?? m.dateFrom,
        dateTo: (m.dateTo as any)?.format?.('YYYY-MM-DD') ?? m.dateTo,
      }))
      await imiExportMutation.mutateAsync({
        importId: actualImportId,
        options: { models: serializedModels },
        taskId,
      })
      useImiExportErrorsStore.getState().clearErrors(companyId, checkedDrivers)
      addTask(taskId, TaskType.ImiExport, null)

      toast.success({
        text: t('imiExport.validation-success'),
        content: t('imiExport.validation-success-details'),
      })
    } catch (error: any) {
      toast.error({
        text: t('imiExport.export-error'),
        content: error.message || t('imiExport.export-error-details'),
      })
    }
  }

  const updateModel = (index: number, updates: Partial<ImiExportModel>) => {
    setModels((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ...updates } : m)),
    )
  }

  const assignToAll = () => {
    setModels((prev) =>
      prev.map((m) => ({
        ...m,
        operationType: [...defaultOperationType],
        transportType: [...defaultTransportType],
        vehicles: [...defaultVehicles],
        country: [...defaultCountry],
        dateFrom: defaultDateFrom.clone(),
        dateTo: defaultDateTo.clone(),
      })),
    )
  }

  const filteredCountries = countries.filter((c) =>
    EU_COUNTRIES.includes(c.code),
  )

  if (models.length === 0) {
    return (
      <section className="tw-flex tw-h-full tw-min-h-fit tw-flex-col tw-items-center tw-justify-center tw-gap-4">
        <p className="tw-text-content-base">{t('imiExport.no-data')}</p>
        <div className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 tw-shadow-sm">
          <div className="tw-border-b tw-border-base-300 tw-bg-content-primary tw-px-4 tw-py-3">
            <h3 className="tw-text-sm tw-font-semibold tw-text-content-base">
              {t('menu.export-list')}
            </h3>
          </div>
          <div className="tw-max-h-64 tw-overflow-y-auto">
            <TableProvider defaultSelected={memoizedDefaultSelectedDrivers}>
              <PrintoutDriversTable
                onSelectionChange={handleDriversSelectionChange}
                initialSelectedIds={memoizedDefaultSelectedDrivers}
              />
            </TableProvider>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="tw-flex tw-h-full tw-min-h-fit tw-flex-col tw-gap-4 tw-overflow-y-auto">
      {/* Summary Row */}
      <div className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 tw-p-4">
        <div className="tw-grid tw-grid-cols-7 tw-gap-4">
          <div className="tw-col-span-1">
            <button
              onClick={assignToAll}
              className="tw-rounded tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-text-white hover:tw-opacity-90"
            >
              {t('imiExport.assign')}
            </button>
          </div>
          <div className="tw-col-span-2">
            <input
              type="date"
              value={defaultDateFrom.format('YYYY-MM-DD')}
              onChange={(e) => setDefaultDateFrom(moment(e.target.value))}
              className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
            />
            <span className="tw-mx-2">—</span>
            <input
              type="date"
              value={defaultDateTo.format('YYYY-MM-DD')}
              onChange={(e) => setDefaultDateTo(moment(e.target.value))}
              className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
            />
          </div>
          <div className="tw-col-span-1">
            <select
              className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
              multiple
              value={defaultCountry.map((c) => c.code)}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map(
                  (opt) => filteredCountries.find((c) => c.code === opt.value),
                ).filter((c): c is CountryModel => c !== undefined)
                setDefaultCountry(selected)
              }}
            >
              {filteredCountries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="tw-col-span-1">
            <select
              className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
              multiple
              value={defaultOperationType}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map(
                  (opt) => opt.value as OperationType,
                )
                setDefaultOperationType(selected)
              }}
            >
              {OPERATION_TYPES.map((ot) => (
                <option key={ot} value={ot}>
                  {getOperationTypeName(ot, t)}
                </option>
              ))}
            </select>
          </div>
          <div className="tw-col-span-1">
            <select
              className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
              multiple
              value={defaultTransportType}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map(
                  (opt) => opt.value as TransportType,
                )
                setDefaultTransportType(selected)
              }}
            >
              {TRANSPORT_TYPES.map((tt) => (
                <option key={tt} value={tt}>
                  {getTransportTypeName(tt, t)}
                </option>
              ))}
            </select>
          </div>
          <div className="tw-col-span-1">
            <select
              className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
              multiple
              value={defaultVehicles}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map(
                  (opt) => opt.value,
                )
                setDefaultVehicles(selected)
              }}
            >
              {vehicles.map((v) => (
                <option key={v.registrationNumber} value={v.registrationNumber}>
                  {v.registrationNumber}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Models Table */}
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead>
            <tr className="tw-border-b tw-border-base-300 tw-bg-content-primary">
              <th className="tw-px-4 tw-py-2 tw-text-left tw-text-xs tw-font-semibold">
                {t('imiExport.driver').toUpperCase()}
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-left tw-text-xs tw-font-semibold">
                {t('imiExport.status').toUpperCase()}
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-left tw-text-xs tw-font-semibold">
                {t('imiExport.date').toUpperCase()}
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-left tw-text-xs tw-font-semibold">
                {t('imiExport.country').toUpperCase()}
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-left tw-text-xs tw-font-semibold">
                {t('imiExport.operation-type').toUpperCase()}
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-left tw-text-xs tw-font-semibold">
                {t('imiExport.transport-type').toUpperCase()}
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-left tw-text-xs tw-font-semibold">
                {t('imiExport.vehicles').toUpperCase()}
              </th>
            </tr>
          </thead>
          <tbody>
            {models.map((model, index) => {
              const isValid = validateModel(model)
              const duration = model.dateFrom && model.dateTo
                ? getDifferenceBetweenDatesInDays(model.dateFrom, model.dateTo)
                : '-'

              return (
                <tr
                  key={index}
                  className={`tw-border-b tw-border-base-300 ${
                    (submitted && !isValid) || exportErrors[model.driver.id] ? 'tw-bg-red-50' : ''
                  }`}
                >
                  <td className="tw-px-4 tw-py-2">
                    {model.driver.surname} {model.driver.name}
                  </td>
                  <td className="tw-px-4 tw-py-2">
                    {model.validation && model.validation.length > 0 ? (
                      <span className="tw-text-red-600">
                        {t('imiExport.status-invalid')}
                        <span className="tw-block tw-max-w-[260px] tw-text-xs">
                          {model.validation
                            .map((key: string) => t(`imiExport.validation.${key}`))
                            .join(', ')}
                        </span>
                      </span>
                    ) : exportErrors[model.driver.id] ? (
                      <span className="tw-text-red-600">
                        {t('imiExport.status-api-error')}
                        <span className="tw-block tw-max-w-[260px] tw-break-words tw-text-xs">
                          {exportErrors[model.driver.id]}
                        </span>
                      </span>
                    ) : (
                      <span className="tw-text-green-600">
                        {t('imiExport.validation-success')}
                      </span>
                    )}
                  </td>
                  <td className="tw-px-4 tw-py-2">
                    <div className="tw-flex tw-gap-2">
                      <input
                        type="date"
                        value={model.dateFrom?.format('YYYY-MM-DD') || ''}
                        onChange={(e) =>
                          updateModel(index, { dateFrom: moment(e.target.value) })
                        }
                        className="tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
                      />
                      <span>—</span>
                      <input
                        type="date"
                        value={model.dateTo?.format('YYYY-MM-DD') || ''}
                        onChange={(e) =>
                          updateModel(index, { dateTo: moment(e.target.value) })
                        }
                        className="tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
                      />
                    </div>
                    {submitted && duration !== '-' && duration > 183 && (
                      <span className="tw-text-xs tw-text-red-600">
                        {t('imiExport.validation.dateFrom')}
                      </span>
                    )}
                  </td>
                  <td className="tw-px-4 tw-py-2">
                    <select
                      multiple
                      value={model.country?.map((c) => c.code) || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map(
                          (opt) => filteredCountries.find((c) => c.code === opt.value),
                        ).filter((c): c is CountryModel => c !== undefined)
                        updateModel(index, { country: selected })
                      }}
                      className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
                    >
                      {filteredCountries.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="tw-px-4 tw-py-2">
                    <select
                      multiple
                      value={model.operationType || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map(
                          (opt) => opt.value as OperationType,
                        )
                        updateModel(index, { operationType: selected })
                      }}
                      className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
                    >
                      {OPERATION_TYPES.map((ot) => (
                        <option key={ot} value={ot}>
                          {getOperationTypeName(ot, t)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="tw-px-4 tw-py-2">
                    <select
                      multiple
                      value={model.transportType || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map(
                          (opt) => opt.value as TransportType,
                        )
                        updateModel(index, { transportType: selected })
                      }}
                      className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
                    >
                      {TRANSPORT_TYPES.map((tt) => (
                        <option key={tt} value={tt}>
                          {getTransportTypeName(tt, t)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="tw-px-4 tw-py-2">
                    <select
                      multiple
                      value={model.vehicles || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map(
                          (opt) => opt.value,
                        )
                        updateModel(index, { vehicles: selected })
                      }}
                      className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1"
                    >
                      {vehicles.map((v) => (
                        <option key={v.registrationNumber} value={v.registrationNumber}>
                          {v.registrationNumber}
                        </option>
                      ))}
                    </select>
                    {submitted && (!model.vehicles || model.vehicles.length === 0) && (
                      <span className="tw-text-xs tw-text-red-600">
                        {t('imiExport.validation.vehicles')}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Export Button */}
      <div className="tw-flex tw-justify-center tw-p-4">
        <button
          onClick={handleExport}
          className="tw-rounded tw-bg-primary tw-px-8 tw-py-2 tw-text-white hover:tw-opacity-90"
        >
          {t('operation.export')}
        </button>
      </div>
    </section>
  )
}

const IMIListPage: React.FC<RouteComponentProps<MatchParams>> = (props) => {
  return (
    <TableProvider>
      <IMIListPageContent {...props} />
    </TableProvider>
  )
}

export default IMIListPage
export { IMIListPage }
